import { localizedCountryName } from '@pmi.web/countries';
import { LabelledText } from 'components/text';
import { useAutoshipCancellationI18n } from 'features/dashboard/hooks/useAutoshipCancellationI18n';
import { useAutoshipData } from 'features/dashboard/hooks/useAutoshipData';
import { useMandatorData } from 'features/dashboard/hooks/useMandatorData';
import { PII, usePII } from 'features/pii';
import { Icon, IconName } from 'libs/icons';
import { useTranslation } from 'libs/translations';
import _ from 'lodash';

interface IAutoshipDetailsProps {
  readonly referenceNumber: string;
}

export function AutoshipDetails(props: IAutoshipDetailsProps) {
  const { t, activeLanguage } = useTranslation();
  const { referenceNumber } = props;

  const { autoshipData, autoshipDataLoading } = useAutoshipData({
    autoshipId: referenceNumber
  });

  const translateCancelationReason = useAutoshipCancellationI18n();
  const { mandatorData, mandatorDataLoading } = useMandatorData({
    mandatorId: autoshipData?.mandator.toString()
  });
  const { pii } = usePII();

  return (
    <>
      {autoshipDataLoading && (
        <>
          <div className="bg-lightGray h-12 w-full animate-pulse" />
          <div className="bg-lightGray h-12 w-full animate-pulse" />
          <div className="bg-lightGray h-20 w-1/2 animate-pulse" />
        </>
      )}
      {!autoshipDataLoading && autoshipData && (
        <>
          {autoshipData.cancel &&
            translateCancelationReason(autoshipData.cancel.reasonId) && (
              <LabelledText
                label={t('Cancellation Reason')}
                value={
                  <span className="pmi-caption">
                    {translateCancelationReason(autoshipData.cancel.reasonId)}
                  </span>
                }
              />
            )}
          <LabelledText
            label={t('Products')}
            value={
              <ul>
                {autoshipData.items.map((item, index) => (
                  <li
                    key={`autoship_item_${index}`}
                    className={index !== 0 ? 'mt-2' : ''}
                  >
                    <div className="flex-grow">
                      <span>{item.name ?? item.articleNumber}</span>
                      <p className="pmi-caption -mt-0.5">
                        {t('Quantity: {{number}}', {
                          number: item.quantity
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            }
          />
          <div className="flex flex-row gap-6">
            {!mandatorData && mandatorDataLoading && (
              <div className="bg-lightGray h-20 w-1/3 animate-pulse" />
            )}
            {mandatorData && (
              <LabelledText
                label={t('Managed in')}
                value={
                  <div>
                    {mandatorData.companyName && (
                      <p>
                        <PII type="address" value={mandatorData.companyName} />
                      </p>
                    )}

                    {!_.isUndefined(mandatorData.country) && (
                      <p className="pmi-caption">
                        {localizedCountryName(
                          pii(mandatorData.country, 'country'),
                          activeLanguage
                        )}
                      </p>
                    )}

                    {mandatorData.phone1 && (
                      <a
                        href={`tel:${pii(
                          mandatorData.phone1.replace(/[() -]/g, ''),
                          'phone'
                        )}`}
                        className="hidden pmi-caption flex-row gap-2 items-center mt-1"
                      >
                        <Icon name={IconName.Call} size={12} />
                        <PII value={mandatorData.phone1} type="phone" />
                      </a>
                    )}
                    {mandatorData.email1 && (
                      <a
                        href={`mailto:${pii(mandatorData.email1, 'email')}`}
                        className=" hidden pmi-caption  flex-row gap-2 items-center"
                      >
                        <Icon name={IconName.Email} size={12} />
                        <PII value={mandatorData.email1} type="email" />
                      </a>
                    )}
                  </div>
                }
              />
            )}
            <LabelledText
              label={t('Reference number')}
              value={referenceNumber}
            />
          </div>
        </>
      )}
    </>
  );
}
